// ---------------------------------------------------------------------------
// When the server will sharpen a photo (SYM-6135), so the app can say so while
// the customer waits. The server owns the decision and does the work; these
// mirror its two rules only to choose a wait message, so a mismatch costs a
// message, never a refusal.
// ---------------------------------------------------------------------------

/** Every ad canvas is at least this on both sides; a smaller photo is enlarged wherever it is used. */
export const SHARPEN_MIN_SIDE = 1080;

/** The smallest picture fal will animate, on either side (SYM-6073). */
const BRING_TO_LIFE_MIN_SIDE = 300;

/** What a story (9:16) and a feed (4:5) crop need at that minimum width. */
const BRING_TO_LIFE_NEEDED: Record<string, { width: number; height: number }> =
  {
    feed: { width: 300, height: 375 },
    story: { width: 300, height: 534 },
    reels: { width: 300, height: 534 },
  };

/** The upload will be sharpened: either side under the canvas minimum. */
export const isSmallPhoto = (
  width?: number | null,
  height?: number | null
): boolean =>
  !!width &&
  !!height &&
  (width < SHARPEN_MIN_SIDE || height < SHARPEN_MIN_SIDE);

/**
 * Bring to Life will sharpen this picture before animating it: the picture
 * itself is too small (under 300 on a side, or too small for the placement's
 * shape). A picture that is large enough is never sharpened there, even when
 * its crop is too small; the crop is the fix in that case.
 */
export const bringToLifeWillSharpen = (
  width: number | null | undefined,
  height: number | null | undefined,
  placement: string | null | undefined
): boolean => {
  if (!width || !height) return false;
  if (width < BRING_TO_LIFE_MIN_SIDE || height < BRING_TO_LIFE_MIN_SIDE) {
    return true;
  }
  const needed = BRING_TO_LIFE_NEEDED[placement ?? ''];
  return !!needed && (width < needed.width || height < needed.height);
};

export const SHARPENING_UPLOAD_MESSAGE =
  'This photo is small, so we are sharpening it. This takes about 15 seconds.';

export const SHARPENING_BRING_TO_LIFE_MESSAGE =
  'This picture is small, so we are sharpening it first. This takes about 15 seconds.';

/** The pixel size of an image file, read in the browser; null when it cannot be read. */
export const readImageSize = (
  file: File
): Promise<{ width: number; height: number } | null> =>
  new Promise((resolve) => {
    if (!file.type.startsWith('image/') || typeof URL === 'undefined') {
      resolve(null);
      return;
    }
    const url = URL.createObjectURL(file);
    const image = new Image();
    image.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
    };
    image.onerror = () => {
      URL.revokeObjectURL(url);
      resolve(null);
    };
    image.src = url;
  });
